import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getMySubscription, daysUntil, statusLabel } from '@/lib/billing';
import type { OrgSubscription } from '@/lib/types';

/**
 * PlanBadge — small chip rendered next to the org chip in the AppShell.
 *
 * Always visible (per Jason 2026-04-27). Renders one of:
 *   "Trial · 23d"   (trial, days remaining)
 *   "Pilot · 87d"   (design-partner cohort)
 *   "Standard"     (paying)
 *   "Past due"     (red — needs attention)
 *   "Canceled"     (gray)
 *   nothing        (no subscription row yet — should not happen post-0074)
 *
 * Click navigates to /billing.
 */
export function PlanBadge() {
  const [sub, setSub] = useState<OrgSubscription | null>(null);

  useEffect(() => {
    let cancelled = false;
    getMySubscription()
      .then((s) => { if (!cancelled) setSub(s); })
      .catch(() => { /* non-fatal — badge just won't render */ });
    return () => { cancelled = true; };
  }, []);

  if (!sub) return null;

  const { label, tone } = describe(sub);
  return (
    <Link
      to="/billing"
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${tone}`}
      aria-label={`Plan: ${label}. Open billing.`}
      title={`Plan: ${label}. Click to manage billing.`}
    >
      {label}
    </Link>
  );
}

function describe(sub: OrgSubscription): { label: string; tone: string } {
  const TONE = {
    neutral: 'border-border text-muted-foreground bg-muted/40 hover:bg-muted/60',
    info:    'border-blue-200 text-blue-900 bg-blue-50 hover:bg-blue-100',
    success: 'border-emerald-200 text-emerald-900 bg-emerald-50 hover:bg-emerald-100',
    warn:    'border-amber-200 text-amber-900 bg-amber-50 hover:bg-amber-100',
    danger:  'border-red-200 text-red-900 bg-red-50 hover:bg-red-100',
    muted:   'border-border text-muted-foreground bg-muted/40 hover:bg-muted/60',
  };
  switch (sub.status) {
    case 'trial': {
      const d = daysUntil(sub.trial_ends_at);
      return { label: d !== null ? `Trial · ${d}d` : 'Trial', tone: TONE.info };
    }
    case 'pilot': {
      const d = daysUntil(sub.pilot_ends_at);
      return { label: d !== null ? `Pilot · ${d}d` : 'Pilot', tone: TONE.success };
    }
    case 'active': {
      return {
        label: sub.tier === 'pro' ? 'Pro' : sub.tier === 'enterprise' ? 'Enterprise' : 'Standard',
        tone: TONE.success,
      };
    }
    case 'past_due':
      return { label: 'Past due', tone: TONE.danger };
    case 'canceled':
      return { label: 'Canceled', tone: TONE.muted };
    default:
      return { label: statusLabel(sub.status), tone: TONE.neutral };
  }
}
